import React, {useState, useEffect, useContext, useCallback, useMemo} from 'react';
import {map, range, forEach, assign, filter, isFunction, reduce} from 'lodash';

import {bindKey, unbindKey} from '@rwh/keystrokes';

const HotkeyContext = React.createContext(null);

let handlerCounter = 0;

const FUNCTION_KEYS = map(range(20), (i) => 'F' + i);

// the two objects below define which hotkeys can be handled, depending of what kind of element currently
// has focus. For example, if the focus is currently on a <button>, then the 'Enter' key should be handled
// by that button, and not by a custom hotkey
const allowedHotkeys = {
    INPUT: [...FUNCTION_KEYS],
    TEXTAREA: [...FUNCTION_KEYS],
    SELECT: [...FUNCTION_KEYS],
};
const disallowedHotkeys = {
    BUTTON: ['Space', 'Enter'],
    A: ['Space', 'Enter'],
};

// Check if a given key press should trigger the custom handler, or if it will be handled by the element
// that is currently in focus, according to the whitelist/blacklist above
function alreadyHandled(event) {
    const focusedElement = event.originalEvent.target.nodeName;

    if (focusedElement in allowedHotkeys) {
        return !allowedHotkeys[focusedElement].some((k) => k == event.key);
    }

    if (focusedElement in disallowedHotkeys) {
        return disallowedHotkeys[focusedElement].some((k) => k == event.key);
    }

    // if the focus is not in any of the special HTML elements, then we will allow the custom handler
    // to run, because we assume that whatever is in focus now won't handle it
    return false;
}

function rebindKeys(handlers, newHotkeys) {
    // The `actualMappings` is essentially a flattening of the `newHotkeys` list.
    // The `newHotkeys' is a list of all calls to `useHotkeys`, so there can be multiple entries in there that
    // want to handle a particular key. The `actualMappings` contains only the hotkey handlers that are
    // active at the moment
    const actualMappings = reduce(newHotkeys, (am, km) => assign(am, km.keyMapping), {});

    // we un-bind all active keystroke handlers, and re-bind the existing ones. This may be
    // some un-necessary work, but it is easier than keeping track of which ones changed since
    // the last call to `rebindKeys`
    const newHandlers = {};
    forEach(handlers, (handler, key) => unbindKey(key, handler));
    forEach(actualMappings, (fn, key) => {
        const wrappedFn = (event) => {
            if (alreadyHandled(event)) return;
            return fn(event);
        };
        bindKey(key, wrappedFn);
        newHandlers[key] = wrappedFn;
    });

    return {
        handlers: newHandlers,
        hotkeys: newHotkeys,
    };
}

// This HOC should be used exactly once at the root of an application. If it is used multiple times, this can
// lead to multiple hotkey handlers being activated for a single key press.
export function WithHotkeys({children}) {
    const [, setHotkeys] = useState({
        hotkeys: [], // complete list of all calls to `useHotkeys`
        handlers: {}, // mapping of key to the function that will run when the key is pressed
    });

    const registerHotkeys = useCallback(
        (keyMapping) => {
            const index = handlerCounter++;

            setHotkeys(({hotkeys, handlers}) => {
                const newHotkeys = [...hotkeys, {index, keyMapping}];
                return rebindKeys(handlers, newHotkeys);
            });

            return index;
        },
        [setHotkeys],
    );

    const unregisterHotkeys = useCallback(
        (index) => {
            setHotkeys(({hotkeys, handlers}) => {
                const newHotkeys = filter(hotkeys, (k) => k.index != index);
                return rebindKeys(handlers, newHotkeys);
            });
        },
        [setHotkeys],
    );

    const context = useMemo(() => ({registerHotkeys, unregisterHotkeys}), [registerHotkeys, unregisterHotkeys]);

    // due to the `useCallback` and `useMemo`, the values in this context actually never change, even when
    // new hotkeys are registered, or hotkeys are de-registered.
    return <HotkeyContext.Provider value={context}>{children}</HotkeyContext.Provider>;
}

// Hook to register hotkey handlers. The first argument, `keyMappingCreator`, can either be a mapping from
// a key name to a handler function, or a function that returns such a mapping. The second argument, `deps`, is
// a `useEffect`-style dependency array, which you can use when the `keyMappingCreator` needs to be re-evaluated
// when the value of some variable that the keystroke handler closes over changes.
export function useHotkeys(keyMappingCreator, deps) {
    const {registerHotkeys, unregisterHotkeys} = useContext(HotkeyContext);

    useEffect(() => {
        // add hotkeys to the context
        const keyMapping = isFunction(keyMappingCreator) ? keyMappingCreator() : keyMappingCreator;
        const hotkeyIndex = registerHotkeys(keyMapping);

        return () => {
            // remove hotkeys from the context
            unregisterHotkeys(hotkeyIndex);
        };
    }, deps ?? []);
}
